import type React from 'react';
import { forwardRef } from 'react';
import { DateLikeInput } from './date-input';
import type { DateLikeProps } from './types';

export interface DateTimeInputProps extends DateLikeProps {
  /**
   * The earliest date time to accept, in the `YYYY-MM-DDTHH:mm` format.
   */
  min?: string;

  /**
   * The latest date time to accept, in the `YYYY-MM-DDTHH:mm` format.
   */
  max?: string;

  /**
   * What type of information to autocomplete in the input.
   */
  autoComplete?: string;

  /**
   * Default value of an uncontrolled input.
   */
  defaultValue?: string;

  /**
   * Value of the input.
   *
   * Makes the input controlled.
   */
  value?: string;

  /**
   * Fires when the input’s value is changed.
   */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
}

/**
 * Lets the users select a date from the native calendar dropdown.
 * Should be used when it is helpful for the user to see what weekday a selected date is.
 *
 * Browser compatibility:
 * - https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/datetime-local#browser_compatibility
 * - As of 24 Sep 2024, Safari and Firefox do not show the visual date picker, but the functionality is still available via the keyboard.
 * - Be careful with this component on pages with broader public audiences.
 */
export const DateTimeInput = forwardRef<HTMLInputElement, DateTimeInputProps>(
  function DateTimeInput(
    props: DateTimeInputProps,
    ref: React.ForwardedRef<HTMLInputElement>,
  ) {
    return <DateLikeInput {...props} type="datetime-local" ref={ref} />;
  },
);
